import { coverage, groupExtensions, installLink, type DeviceInventory, type DeviceRecord, type ExtensionGroup } from '@bsync/shared';
import { A } from '@solidjs/router';
import { For, Match, Show, Switch, createMemo, createSignal } from 'solid-js';
import { BrowserMark, IconCheck, IconExternal, IconSearch } from '../components/Icons';
import { state } from '../lib/app';

type Filter = 'missing' | 'all';

export default function ExtensionsPage() {
  const [filter, setFilter] = createSignal<Filter>('missing');
  const [query, setQuery] = createSignal('');

  const browsers = createMemo<DeviceRecord[]>(() => (state.devices() ?? []).filter((d) => d.browser !== 'web'));
  const groups = createMemo<ExtensionGroup[]>(() => {
    const devices = browsers();
    const exts = state.extensions() ?? [];
    const inventories: DeviceInventory[] = devices.map((device) => ({ device, extensions: exts.filter((e) => e.deviceId === device.id) }));
    return groupExtensions(inventories);
  });
  const ids = () => browsers().map((d) => d.id);
  const visible = createMemo(() => {
    const q = query().trim().toLowerCase();
    return groups().filter((g) => (filter() === 'all' || coverage(g, ids()) === 'partial') && (!q || g.name.toLowerCase().includes(q)));
  });
  const missingCount = (deviceId: string) => groups().filter((g) => !g.installs.has(deviceId)).length;
  const loading = () => state.devices() === null || state.extensions() === null;

  return (
    <div class="page">
      <header class="page-head">
        <div class="head-row">
          <h1>Extensions</h1>
        </div>
        <p class="lede">
          What’s installed in each connected browser. Chrome and Firefox builds of the same extension are matched even though their ids differ. Installing
          always happens in the browser’s own store; bsync never installs anything for you.
        </p>
      </header>

      <Switch>
        <Match when={loading()}>
          <div class="list" aria-busy="true">
            <For each={[0, 1, 2, 3]}>{() => <div class="row skeleton"><span class="tile" /><div class="row-main"><span class="bar w60" /><span class="bar w30" /></div></div>}</For>
          </div>
        </Match>
        <Match when={browsers().length === 0}>
          <div class="empty">
            <h2>No browsers connected yet</h2>
            <p>Install the bsync extension in Chrome or Firefox and sign in. Each browser then reports its extensions here.</p>
            <A class="btn" href="/devices">
              Go to Devices
            </A>
          </div>
        </Match>
        <Match when={true}>
          <div class="device-summary">
            <For each={browsers()}>
              {(d) => (
                <div class="summary-chip">
                  <BrowserMark browser={d.browser} />
                  <div>
                    <strong>{d.name}</strong>
                    <span class="muted small">
                      {(state.extensions() ?? []).filter((e) => e.deviceId === d.id && e.type === 'extension').length} installed
                      <Show when={browsers().length > 1}>, {missingCount(d.id)} missing</Show>
                    </span>
                  </div>
                </div>
              )}
            </For>
          </div>

          <Show when={browsers().length === 1}>
            <div class="notice">Connect a second browser to compare. Until then this shows what {browsers()[0]!.name} has installed.</div>
          </Show>

          <div class="toolbar">
            <div class="segmented" role="radiogroup" aria-label="Filter">
              <button role="radio" aria-checked={filter() === 'missing'} classList={{ on: filter() === 'missing' }} onClick={() => setFilter('missing')}>
                Missing somewhere
              </button>
              <button role="radio" aria-checked={filter() === 'all'} classList={{ on: filter() === 'all' }} onClick={() => setFilter('all')}>
                All ({groups().length})
              </button>
            </div>
            <label class="search compact">
              <IconSearch />
              <input type="search" placeholder="Filter by name" value={query()} onInput={(e) => setQuery(e.currentTarget.value)} aria-label="Filter extensions" />
            </label>
          </div>

          <Show
            when={visible().length}
            fallback={
              <div class="empty">
                <Show when={filter() === 'missing' && !query()} fallback={<h2>No extensions match “{query()}”</h2>}>
                  <h2>Everything matches</h2>
                  <p>Every extension is installed in all of your connected browsers.</p>
                </Show>
              </div>
            }
          >
            <div class="matrix-wrap">
              <table class="matrix">
                <thead>
                  <tr>
                    <th scope="col">Extension</th>
                    <For each={browsers()}>
                      {(d) => (
                        <th scope="col">
                          <span class="th-device">
                            <BrowserMark browser={d.browser} /> <span class="ellipsis">{d.name}</span>
                          </span>
                        </th>
                      )}
                    </For>
                  </tr>
                </thead>
                <tbody>
                  <For each={visible()}>
                    {(g) => (
                      <tr>
                        <th scope="row">
                          <div class="ext-name">{g.name}</div>
                          <Show when={g.homepageUrl}>
                            <a class="muted small" href={g.homepageUrl!} target="_blank" rel="noopener noreferrer">
                              Website
                            </a>
                          </Show>
                        </th>
                        <For each={browsers()}>
                          {(d) => {
                            const inst = () => g.installs.get(d.id);
                            const link = () => installLink(g, d.browser);
                            return (
                              <td>
                                <Show
                                  when={inst()}
                                  fallback={
                                    <Show when={link()}>
                                      <a class="install" href={link()!.url} target="_blank" rel="noopener noreferrer" title={link()!.kind === 'search' ? 'No exact match known: opens a store search' : 'Opens the store page'}>
                                        {link()!.kind === 'search' ? 'Find in store' : 'Install'} <IconExternal />
                                      </a>
                                    </Show>
                                  }
                                >
                                  <span class="installed" classList={{ off: !inst()!.enabled }}>
                                    <IconCheck /> {inst()!.version}
                                    <Show when={!inst()!.enabled}>
                                      <span class="tag">off</span>
                                    </Show>
                                  </span>
                                </Show>
                              </td>
                            );
                          }}
                        </For>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
        </Match>
      </Switch>
    </div>
  );
}
